import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { submitN8nForm } from '@/services/n8nService';

export const fieldLabel = "block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-1.5";
// 16px text so iOS Safari does not zoom into the field
export const fieldInput = "w-full bg-white dark:bg-zinc-950 border border-slate-300 dark:border-zinc-700 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-600 px-4 py-3 text-base rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition";
export const primaryButton = "btn-primary text-white rounded-full h-12 px-8 text-base";
export const textLink = "text-sm text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200 underline underline-offset-4 decoration-slate-300 dark:decoration-zinc-700 transition-colors";

export const SuccessPanel = ({ title, message, actionLabel, onAction }: { title: string; message: string; actionLabel: string; onAction: () => void }) => (
    <div className="flex w-full flex-col items-center gap-6 py-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
        </div>
        <div className="space-y-2">
            <h3 className="text-3xl font-light text-slate-900 dark:text-white">{title}</h3>
            <p className="font-light text-slate-600 dark:text-zinc-400">{message}</p>
        </div>
        <Button onClick={onAction} className={`w-full sm:w-auto ${primaryButton}`}>
            {actionLabel}
        </Button>
    </div>
);

interface PatientSupportFormProps {
    /** Heading shown above the form (hidden once the message is sent) */
    header?: React.ReactNode;
    /** Shows a "Go back" link under the form */
    onBack?: () => void;
    /** Button on the success screen, e.g. "Back to start" or "Close" */
    successActionLabel: string;
    onSuccessAction: () => void;
    /** Prefix for field ids, so two copies of the form never share ids */
    idPrefix?: string;
}

/** The patient "contact support" form, used in the invite-code flow and in the footer's Contact dialog. */
export function PatientSupportForm({ header, onBack, successActionLabel, onSuccessAction, idPrefix = 'support' }: PatientSupportFormProps) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [clinic, setClinic] = useState("");
    const [reason, setReason] = useState("I need an invite code");
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMessage("");

        try {
            const finalMessage = message.trim() ? `${reason}: ${message.trim()}` : reason;

            const result = await submitN8nForm({
                type: 'support',
                source: 'clinic_contact_support',
                name: name.trim() || null,
                email: email.trim() || null,
                clinic_name: clinic.trim() || null,
                topic: reason,
                message: finalMessage,
            });

            if (result.success) {
                setStatus('success');
                setName("");
                setEmail("");
                setClinic("");
                setReason("I need an invite code");
                setMessage("");
            } else {
                setStatus('error');
                setErrorMessage(result.error || "Failed to send message. Try again.");
            }
        } catch (error: any) {
            console.error("Support submission error:", error);
            setStatus('error');
            setErrorMessage("Failed to send message. Try again.");
        }
    };

    const handleSuccessAction = () => {
        setStatus('idle');
        onSuccessAction();
    };

    if (status === 'success') {
        return <SuccessPanel title="Message sent!" message="Thanks! We'll be in touch shortly." actionLabel={successActionLabel} onAction={handleSuccessAction} />;
    }

    return (
        <>
            {header}
            <form onSubmit={handleSubmit} className="w-full space-y-4 text-left">
                <div>
                    <label htmlFor={`${idPrefix}_name`} className={fieldLabel}>Name *</label>
                    <input
                        id={`${idPrefix}_name`}
                        name="name"
                        type="text"
                        autoComplete="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your full name"
                        className={fieldInput}
                        required
                    />
                </div>
                <div>
                    <label htmlFor={`${idPrefix}_email`} className={fieldLabel}>Email *</label>
                    <input
                        id={`${idPrefix}_email`}
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className={fieldInput}
                        required
                    />
                </div>
                <div>
                    <label htmlFor={`${idPrefix}_clinic`} className={fieldLabel}>Clinic (Optional)</label>
                    <input
                        id={`${idPrefix}_clinic`}
                        name="clinic"
                        type="text"
                        autoComplete="organization"
                        value={clinic}
                        onChange={(e) => setClinic(e.target.value)}
                        placeholder="Name of your clinic or practice"
                        className={fieldInput}
                    />
                </div>
                <div>
                    <label htmlFor={`${idPrefix}_reason`} className={fieldLabel}>Reason *</label>
                    <select
                        id={`${idPrefix}_reason`}
                        name="reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className={fieldInput}
                        required
                    >
                        <option value="I need an invite code">I need an invite code</option>
                        <option value="Account Login Issues">Account Login Issues</option>
                        <option value="Content & Video Question">Content & Video Question</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
                <div>
                    <label htmlFor={`${idPrefix}_message`} className={fieldLabel}>Message (Optional)</label>
                    <textarea
                        id={`${idPrefix}_message`}
                        name="message"
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Provide any additional details or questions..."
                        className={fieldInput}
                    />
                </div>

                <Button type="submit" disabled={status === 'loading'} className={`w-full ${primaryButton} mt-2`}>
                    {status === 'loading' ? 'Sending...' : 'Send message'}
                </Button>

                {status === 'error' && <p className="text-center text-sm font-medium text-red-500">{errorMessage || 'Failed to send message. Try again.'}</p>}
            </form>
            {onBack && <button onClick={onBack} className={textLink}>Go back</button>}
        </>
    );
}
